/**
 * session-port.ts — how the engine gets a fresh access token without knowing
 * which app it is running inside.
 *
 * WHY A PORT AND NOT A DIRECT CALL. `sparkly.auth.ts` used to import the kids
 * `authService`, whose `getSession()` returns a MEMORY-CACHED session on
 * purpose: calling the SDK on every request acquires a new auth lock, and on
 * the Sparkly path that showed up as latency. Reaching into `apps/legacy` from a
 * package is not allowed any more, and copying that cache into the engine would
 * give the kids app two of them.
 *
 * So the engine asks through this port. The default goes straight to the shared
 * client and is correct on its own — that is what `apps/student` uses. The kids
 * app overrides it at boot with its cached reader, so nothing about its
 * behaviour changes.
 */
import { getSupabaseClient } from '@theidiomaclub/supabase';

export interface SparklySessionToken {
    readonly access_token: string;
}

export type SparklySessionTokenReader = () => Promise<SparklySessionToken | null>;

const DEFAULT_READER: SparklySessionTokenReader = async () => {
    const client = getSupabaseClient();
    if (!client) return null;
    const { data } = await client.auth.getSession();
    return data.session ? { access_token: data.session.access_token } : null;
};

let reader: SparklySessionTokenReader = DEFAULT_READER;

/** Called once at app boot when the app has a better (cached) reader. */
export function setSparklySessionTokenReader(next: SparklySessionTokenReader): void {
    reader = next;
}

/** Used by the engine. Never throws — a missing session falls back to the
 *  `localStorage` path the caller already implements. */
export async function readSparklySessionToken(): Promise<SparklySessionToken | null> {
    try {
        return await reader();
    } catch {
        return null;
    }
}
